import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AbcRoundedIcon from '@mui/icons-material/AbcRounded';
import { formatInt } from '../../utils/format';
import WidgetShell from './WidgetShell';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function LetterFrequencyWidget({ stats }) {
  const letterFrequency = React.useMemo(() => {
    const freq = {};
    Object.entries(stats?.words?.frequency || {}).forEach(([word, count]) => {
      String(word || '')
        .toUpperCase()
        .replace(/[^A-Z]/g, '')
        .split('')
        .forEach((letter) => {
          freq[letter] = (freq[letter] || 0) + Number(count);
        });
    });
    return freq;
  }, [stats?.words?.frequency]);

  const maxLetterFreq = React.useMemo(
    () => Math.max(1, ...Object.values(letterFrequency).map(Number)),
    [letterFrequency]
  );

  const totalLettersPlayed = React.useMemo(
    () => Object.values(letterFrequency).reduce((a, b) => a + b, 0),
    [letterFrequency]
  );

  return (
    <WidgetShell
      icon={<AbcRoundedIcon color="primary" fontSize="small" />}
      title="Letter Frequency"
      subtitle="How often each letter appears in your submitted words"
      action={
        totalLettersPlayed > 0 ? (
          <Chip
            size="small"
            label={`${formatInt(totalLettersPlayed)} letters`}
            sx={{ fontWeight: 700 }}
          />
        ) : null
      }
    >
      {totalLettersPlayed > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
            gap: 0.7,
          }}
        >
          {ALPHABET.map((letter) => {
            const count = letterFrequency[letter] || 0;
            const ratio = count / maxLetterFreq;
            const bgAlpha = count > 0 ? 0.08 + ratio * 0.82 : 0.04;
            const isHot = ratio > 0.52;
            return (
              <Tooltip
                key={letter}
                title={count > 0 ? `${formatInt(count)} uses` : 'Never played'}
                arrow
              >
                <Box
                  sx={{
                    borderRadius: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 0.9,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, bgAlpha),
                    border: (theme) =>
                      `1px solid ${alpha(theme.palette.primary.main, Math.min(0.45, bgAlpha + 0.1))}`,
                    cursor: 'default',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13,
                      lineHeight: 1,
                      color: isHot ? 'white' : 'text.primary',
                    }}
                  >
                    {letter}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 9,
                      lineHeight: 1.5,
                      color: isHot ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                    }}
                  >
                    {count > 0 ? formatInt(count) : '—'}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      ) : (
        <Typography color="text.secondary" variant="body2">
          No letter data yet. Play some rounds to see your letter tendencies.
        </Typography>
      )}
    </WidgetShell>
  );
}

export default LetterFrequencyWidget;
